// A render error anywhere used to unmount the whole tree and leave a blank page with nothing in app_logs (Studio,
// 2026-10-19). This catches it, logs it, and offers a way back. Leaving the page clears it.
//
// NOT keyed on the path: a key remounts everything under it, and this wraps the whole app - every tab change rebuilt
// Layout, the background, the profile and Find's providers from scratch (2026-10-21: the Events image flash, the
// background restarting on each page, slow group pages). The error is cleared when the path changes instead.
import { Component } from 'react';
import { useLocation } from 'react-router-dom';
import { appLog, flushAppLogs } from '../lib/appLog';
import Button from './ui/Button';

class Boundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prev) {
    if (this.state.error && prev.path !== this.props.path) this.setState({ error: null });
  }

  componentDidCatch(error, info) {
    appLog('error', 'render.crash', {
      path: this.props.path,
      message: String(error?.message ?? error).slice(0, 500),
      stack: String(error?.stack ?? '').slice(0, 2000),
      componentStack: String(info?.componentStack ?? '').slice(0, 2000),
    });
    flushAppLogs('render.crash');
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ background: 'var(--bg-primary, #0b0612)' }}
      >
        <div className="max-w-md text-center" style={{ color: 'var(--text-primary, #fff)' }}>
          <p className="text-lg font-semibold">Something went wrong</p>
          <p className="mt-2 text-sm opacity-70 break-words">{String(error?.message ?? error)}</p>
          <div className="mt-5 flex justify-center gap-3">
            <Button onClick={() => window.location.reload()}>Reload</Button>
            <Button variant="secondary" onClick={() => window.location.assign('/')}>
              Home
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default function AppErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <Boundary path={pathname}>{children}</Boundary>;
}
